import * as yup from 'yup';
import {
  MAX_DEFAULT_FIELD_SYMBOLS,
  MAX_DEFAULT_BIG_FIELD_SYMBOLS,
} from 'constants/general';
import type { TLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.types';

export const getPhoneCaseFormSchema = (localize: TLocalize) => {
  const customFieldOptionSchema = yup.object().shape({
    name: yup
      .string()
      .required(
        localize('validation.admin.phonecase.option.name.required') as string
      )
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        localize('validation.admin.phonecase.option.name.max', [
          String(MAX_DEFAULT_FIELD_SYMBOLS),
        ]) as string
      ),
    color: yup.string().optional(),
  });

  const customFieldSchema = yup.object().shape({
    name: yup
      .string()
      .required(
        localize('validation.admin.phonecase.field.name.required') as string
      )
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        localize('validation.admin.phonecase.field.name.max', [
          String(MAX_DEFAULT_FIELD_SYMBOLS),
        ]) as string
      ),
    type: yup
      .string()
      .required(
        localize('validation.admin.phonecase.field.type.required') as string
      ),
    placeholder: yup
      .string()
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        localize('validation.admin.phonecase.field.placeholder.max', [
          String(MAX_DEFAULT_FIELD_SYMBOLS),
        ]) as string
      ),
    maxLength: yup
      .number()
      .optional()
      .min(
        0,
        localize('validation.admin.phonecase.field.maxlength.min') as string
      )
      .integer(
        localize('validation.admin.phonecase.field.maxlength.integer') as string
      ),
    minLength: yup
      .number()
      .optional()
      .min(
        0,
        localize('validation.admin.phonecase.field.minlength.min') as string
      )
      .integer(
        localize('validation.admin.phonecase.field.minlength.integer') as string
      )
      .test(
        'min-less-than-max',
        localize('validation.admin.phonecase.field.length.order') as string,
        function (value) {
          const { maxLength } = this.parent;
          if (maxLength !== undefined && value !== undefined) {
            return value <= maxLength;
          }
          return true;
        }
      ),
    options: yup.array().of(customFieldOptionSchema).optional(),
  });

  return yup.object().shape({
    name: yup
      .string()
      .required(localize('validation.admin.phonecase.name.required') as string)
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        localize('validation.admin.phonecase.name.max', [
          String(MAX_DEFAULT_FIELD_SYMBOLS),
        ]) as string
      ),
    description: yup
      .string()
      .required(
        localize('validation.admin.phonecase.description.required') as string
      )
      .max(
        MAX_DEFAULT_BIG_FIELD_SYMBOLS,
        localize('validation.admin.phonecase.description.max', [
          String(MAX_DEFAULT_BIG_FIELD_SYMBOLS),
        ]) as string
      ),
    price: yup
      .number()
      .required(localize('validation.admin.phonecase.price.required') as string)
      .min(0.01, localize('validation.admin.phonecase.price.min') as string)
      .typeError(localize('validation.admin.phonecase.price.type') as string)
      .test(
        'not-zero',
        localize('validation.admin.phonecase.price.notzero') as string,
        (value) => {
          return value !== undefined && value !== null && value !== 0;
        }
      ),
    newPrice: yup
      .number()
      .nullable()
      .min(0, localize('validation.admin.phonecase.newprice.min') as string)
      .typeError(localize('validation.admin.phonecase.newprice.type') as string)
      .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null) {
          return null;
        }
        return value;
      }),
    tags: yup.array().of(yup.string()).default([]),
    customFields: yup.array().of(customFieldSchema).default([]),
  });
};
